import { useEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '../api/client'
import { useOrganizationSettings, useSetTheme, useThemeTokenCatalog } from '../api/hooks'
import type { OrganizationTheme } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { BrandLockup, BrandLockupStacked } from '../components/Brand'
import { getDefaultTokens, reapplyTheme } from '../theme/applyTheme'
import { TRACER_ICON_DATA_URI, TRACER_MARK_DATA_URI, tracerTheme } from '../theme/presets'
import { COLOR_LABELS, COLOR_TOKENS, TOKEN_PREFIX } from '../theme/tokens'
import type { ColorToken } from '../theme/tokens'

/*
  The branding screen — what this product is called and what it looks like, per
  organization (white label).

  IT EDITS THE LIVE DOCUMENT, NOT A PREVIEW PANE

  Every colour in the application already comes from a custom property on
  documentElement, so writing one there restyles the whole product instantly — the
  header, the grids, the badges, the screen you are standing on. A preview pane could
  only ever show a handful of components, and the question an admin actually has is
  "does our navy work on the QC dashboard", which a swatch cannot answer.

  The cost is that leaving with unsaved changes would strand the application in a state
  the database does not agree with, so the draft is reverted on unmount and on cancel.
  Saving is what makes it real, and the notice at the top says so.

  ADMIN ONLY, AND NOT IN THE NAVIGATION

  A rebrand happens once per customer, usually before anyone else logs in. A permanent
  nav slot for it would cost every analyst a slot on every screen for something they will
  never open; it is reached from the user menu instead.
*/

/** A token in the draft, or the empty string for "leave it at the product default". */
type Draft = {
  productName: string
  tagline: string
  colors: Record<string, string>
  fontDisplay: string
  fontSans: string
  fontMono: string
  fontStylesheetUrl: string
  markUrl: string
  iconUrl: string
  showWordmark: boolean
}

const EMPTY_DRAFT: Draft = {
  productName: '',
  tagline: '',
  colors: {},
  fontDisplay: '',
  fontSans: '',
  fontMono: '',
  fontStylesheetUrl: '',
  markUrl: '',
  iconUrl: '',
  showWordmark: true,
}

function toDraft(theme: OrganizationTheme | null | undefined): Draft {
  if (!theme) return EMPTY_DRAFT

  return {
    productName: theme.productName ?? '',
    tagline: theme.tagline ?? '',
    colors: { ...(theme.colors ?? {}) },
    fontDisplay: theme.fonts?.display ?? '',
    fontSans: theme.fonts?.sans ?? '',
    fontMono: theme.fonts?.mono ?? '',
    fontStylesheetUrl: theme.fonts?.stylesheetUrl ?? '',
    markUrl: theme.logo?.markUrl ?? '',
    iconUrl: theme.logo?.iconUrl ?? '',
    showWordmark: theme.logo?.showWordmark ?? true,
  }
}

/**
 * Turns a draft into the document the API stores.
 *
 * Empty fields are dropped rather than sent as empty strings. The difference matters:
 * an absent token falls through to the product's default, and an empty string is a
 * value the browser would refuse — so clearing a field has to produce absence or
 * "reset this colour" would quietly do nothing.
 */
function toTheme(draft: Draft): OrganizationTheme | null {
  const colors: Record<string, string> = {}
  for (const [token, value] of Object.entries(draft.colors)) {
    if (value.trim()) colors[token] = value.trim()
  }

  const fonts = {
    ...(draft.fontDisplay.trim() ? { display: draft.fontDisplay.trim() } : {}),
    ...(draft.fontSans.trim() ? { sans: draft.fontSans.trim() } : {}),
    ...(draft.fontMono.trim() ? { mono: draft.fontMono.trim() } : {}),
    ...(draft.fontStylesheetUrl.trim()
      ? { stylesheetUrl: draft.fontStylesheetUrl.trim() }
      : {}),
  }

  const logo = {
    ...(draft.markUrl.trim() ? { markUrl: draft.markUrl.trim() } : {}),
    ...(draft.iconUrl.trim() ? { iconUrl: draft.iconUrl.trim() } : {}),
    ...(draft.markUrl.trim() ? { showWordmark: draft.showWordmark } : {}),
  }

  const theme: OrganizationTheme = {
    ...(draft.productName.trim() ? { productName: draft.productName.trim() } : {}),
    ...(draft.tagline.trim() ? { tagline: draft.tagline.trim() } : {}),
    ...(Object.keys(colors).length > 0 ? { colors } : {}),
    ...(Object.keys(fonts).length > 0 ? { fonts } : {}),
    ...(Object.keys(logo).length > 0 ? { logo } : {}),
  }

  // A theme that overrides nothing is not a theme. Sending null is what clears the row,
  // and it is how an organization stops being white-labelled.
  return Object.keys(theme).length === 0 ? null : theme
}

/** The parts of a draft that describe the marks rather than the design system. */
function logoFieldsOf(draft: Draft): Pick<Draft, 'markUrl' | 'iconUrl' | 'showWordmark'> {
  return { markUrl: draft.markUrl, iconUrl: draft.iconUrl, showWordmark: draft.showWordmark }
}

export function Branding() {
  const { hasRole } = useAuth()
  const isAdmin = hasRole('Admin')

  const settings = useOrganizationSettings()
  const catalog = useThemeTokenCatalog(isAdmin)
  const save = useSetTheme()

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [loaded, setLoaded] = useState(false)
  const [violations, setViolations] = useState<Record<string, string>>({})

  /*
      The values the stylesheet declares, for the placeholder beside each field and for
      the colour picker's starting position on a token nobody has overridden.

      Captured by the theme provider before it wrote anything, not read here: custom
      properties inherit, so once a theme is on documentElement there is no element left
      in the tree that can still see the stylesheet's value.
  */
  const defaults = useMemo(() => getDefaultTokens(), [])

  const saved = settings.data?.theme ?? null

  // Seeded once, from the first successful read. Re-seeding on every refetch would
  // discard whatever the admin had typed the moment the query revalidated.
  useEffect(() => {
    if (!loaded && settings.data) {
      setDraft(toDraft(settings.data.theme))
      setLoaded(true)
    }
  }, [loaded, settings.data])

  /*
      The draft is painted onto the live document as it is typed, and taken back off when
      this screen is left.

      THE DRAFT IS A LAYER ON TOP, AND TAKING IT OFF MEANS PUTTING THE SAVED THEME BACK.

      Both this and `applyTheme` write to documentElement's inline style — the same
      declaration — so `removeProperty` here strips the value whichever of them set it.
      Removing the draft's properties therefore does NOT reveal the saved theme
      underneath; it deletes it. And the provider will not notice, because the theme
      object has not changed and its effect will not re-run, so the organization's colours
      would stay gone until the next actual theme change.

      Hence `reapplyTheme()` after every removal: it re-asserts what the provider last
      applied, and the draft is then painted over the top of it again.
  */
  const previewed = useRef<string[]>([])

  useEffect(() => {
    const root = document.documentElement

    for (const property of previewed.current) {
      root.style.removeProperty(property)
    }
    previewed.current = []

    // Puts the saved theme back before the draft goes over it. Also what makes clearing
    // a single field fall back to the saved colour rather than to the product default.
    reapplyTheme()

    if (!loaded) return

    for (const [token, value] of Object.entries(draft.colors)) {
      if (!value.trim()) continue
      const property = `${TOKEN_PREFIX.colors}${token}`
      root.style.setProperty(property, value.trim())
      previewed.current.push(property)
    }
  }, [draft.colors, loaded])

  useEffect(
    () => () => {
      for (const property of previewed.current) {
        document.documentElement.style.removeProperty(property)
      }
      previewed.current = []
      reapplyTheme()
    },
    [],
  )

  if (!isAdmin) {
    return (
      <div className="p-6">
        <h1 className="mb-1 text-[17px]">Branding</h1>
        <p className="text-[13px]" style={{ color: 'var(--color-muted)' }}>
          Only an administrator can change what this product is called and how it looks.
        </p>
      </div>
    )
  }

  const dirty = JSON.stringify(toTheme(draft)) !== JSON.stringify(saved)

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((current) => ({ ...current, [key]: value }))
  }

  function setColor(token: string, value: string) {
    setDraft((current) => ({ ...current, colors: { ...current.colors, [token]: value } }))
  }

  function clearColor(token: string) {
    setDraft((current) => {
      const colors = { ...current.colors }
      delete colors[token]
      return { ...current, colors }
    })
  }

  function commit() {
    setViolations({})
    save.mutate(toTheme(draft), {
      onError: (error) => {
        if (error instanceof ApiError) setViolations(error.violations)
      },
    })
  }

  function revert() {
    setDraft(toDraft(saved))
    setViolations({})
    save.reset()
  }

  /**
   * Fills the form with the product's own values.
   *
   * Nothing on screen changes — every value in the preset is the value already being
   * rendered. What changes is that the values are now in the form, where an admin can
   * move them two steps toward their own brand instead of starting from thirty empty
   * boxes with no indication of what any of them currently is.
   */
  function loadTracerDefault() {
    setViolations({})
    // The logo fields are left as they are rather than cleared: an admin who has already
    // pasted their mark and now wants the product's palette under it should not lose it
    // to a button labelled "load the defaults".
    setDraft((current) => ({ ...toDraft(tracerTheme(defaults)), ...logoFieldsOf(current) }))
  }

  function clearTheme() {
    setViolations({})
    save.mutate(null, {
      onSuccess: () => setDraft(EMPTY_DRAFT),
      onError: (error) => {
        if (error instanceof ApiError) setViolations(error.violations)
      },
    })
  }

  return (
    <div className="h-full overflow-auto">
      <header
        className="sticky top-0 z-10 flex flex-wrap items-center gap-3 border-b px-4 py-2.5"
        style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule)' }}
      >
        <h1 className="text-[17px]">Branding</h1>

        <span className="text-[12px]" style={{ color: 'var(--color-faint)' }}>
          Colours apply to this window as you type. Nothing reaches anyone else until you
          save.
        </span>

        <div className="ml-auto flex items-center gap-2">
          {dirty && (
            <span
              className="badge"
              style={{ background: 'var(--color-warn-wash)', color: 'var(--color-warn)' }}
            >
              unsaved
            </span>
          )}

          <button className="btn btn-sm" onClick={revert} disabled={!dirty || save.isPending}>
            Discard
          </button>

          <button
            className="btn btn-sm btn-primary"
            onClick={commit}
            disabled={!dirty || save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </header>

      {save.isError && Object.keys(violations).length === 0 && (
        <p
          className="px-4 py-2 text-[12px]"
          style={{ background: 'var(--color-error-wash)', color: 'var(--color-error)' }}
          role="alert"
        >
          {save.error instanceof ApiError ? save.error.message : 'Could not save the theme.'}
        </p>
      )}

      <div className="grid gap-6 p-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex flex-col gap-6">
          <Section
            title="start from"
            hint="A starting point, not a switch. Loading a preset fills the fields below; nothing is stored until you save."
          >
            <div className="flex flex-wrap items-center gap-3">
              <button className="btn btn-sm" onClick={loadTracerDefault} disabled={save.isPending}>
                Load the Tracer default
              </button>

              <span className="text-[11.5px]" style={{ color: 'var(--color-faint)' }}>
                The product's own name, palette, radii, spacing and type — the values every
                screen is already rendering with. Loading them changes nothing on screen; it
                puts them in the form so you can move them toward your own brand.
              </span>
            </div>

            {/*
                The distinction is small and matters exactly once, years later, when the
                product's palette changes and half the customers move with it. Better said
                here than discovered then.
            */}
            <p className="text-[11.5px]" style={{ color: 'var(--color-faint)' }}>
              Saving the preset unchanged is <em>not</em> the same as having no theme. An
              organization with no theme follows Tracer if Tracer's own palette ever changes;
              one that saved these values keeps them. Use{' '}
              <span style={{ color: 'var(--color-muted)' }}>Remove branding</span> below for
              the first.
            </p>
          </Section>

          <Section
            title="name"
            hint="Replaces “Tracer” in the header, the browser tab and the sign-in screen."
          >
            <Field label="Product name" violation={violations['productName']}>
              <input
                className="input"
                value={draft.productName}
                placeholder="Tracer"
                maxLength={60}
                onChange={(e) => set('productName', e.target.value)}
              />
            </Field>

            <Field label="Tagline" violation={violations['tagline']}>
              <input
                className="input"
                value={draft.tagline}
                placeholder="Cost Allocation"
                maxLength={80}
                onChange={(e) => set('tagline', e.target.value)}
              />
            </Field>
          </Section>

          <Section
            title="logo"
            hint={
              catalog.data && catalog.data.allowedAssetHosts.length === 0
                ? 'Paste the image as a data: URI — this deployment does not load images from other hosts. Rendered in an <img>, so an SVG cannot run script.'
                : `An https:// URL from ${catalog.data?.allowedAssetHosts.join(', ') ?? '…'}, or a data: URI. Rendered in an <img>, so an SVG cannot run script.`
            }
          >
            <Field
              label="Mark"
              hint="Shown at the height of the header bar, at whatever width its aspect ratio wants."
              violation={violations['logo.markUrl']}
            >
              <textarea
                className="input input-mono"
                rows={3}
                value={draft.markUrl}
                placeholder="data:image/svg+xml;base64,…"
                onChange={(e) => set('markUrl', e.target.value)}
              />
            </Field>

            <Field
              label="Tab icon"
              hint="Square. Falls back to the mark, which is usually wrong at 16px."
              violation={violations['logo.iconUrl']}
            >
              <textarea
                className="input input-mono"
                rows={2}
                value={draft.iconUrl}
                placeholder="data:image/png;base64,…"
                onChange={(e) => set('iconUrl', e.target.value)}
              />
            </Field>

            <label className="flex items-center gap-2 text-[12.5px]">
              <input
                type="checkbox"
                checked={draft.showWordmark}
                disabled={!draft.markUrl.trim()}
                onChange={(e) => set('showWordmark', e.target.checked)}
              />
              <span style={{ color: draft.markUrl.trim() ? undefined : 'var(--color-faint)' }}>
                Show the product name beside the mark
              </span>
              <span className="text-[11px]" style={{ color: 'var(--color-faint)' }}>
                Turn off if your logo already contains your name.
              </span>
            </label>

            {/*
                The Tracer mark as a fixed image, for the customer keeping the mark and
                changing only the name. Offered rather than pre-filled, and the sentence
                below says why: leaving these blank draws the vector mark, which is the
                better artefact — it recolours with the palette, drops to a three-element
                form below 24px, and reverses cleanly on a dark fill. An <img> does none
                of that.
            */}
            <div
              className="flex flex-wrap items-center gap-2 border-t pt-3"
              style={{ borderColor: 'var(--color-rule-soft)' }}
            >
              <button
                className="btn btn-sm"
                onClick={() => {
                  set('markUrl', TRACER_MARK_DATA_URI)
                  set('iconUrl', TRACER_ICON_DATA_URI)
                }}
              >
                Use the Tracer mark
              </button>

              <button
                className="btn btn-sm"
                onClick={() => {
                  set('markUrl', '')
                  set('iconUrl', '')
                }}
                disabled={!draft.markUrl.trim() && !draft.iconUrl.trim()}
              >
                Clear
              </button>

              <span className="text-[11.5px]" style={{ color: 'var(--color-faint)' }}>
                Both blank is usually right: the mark is then drawn rather than loaded, so it
                takes your palette, survives at favicon size, and reverses on a dark fill.
              </span>
            </div>
          </Section>

          <Section
            title="palette"
            hint="Hex only. Leave a colour blank to keep the product's own."
          >
            <div className="grid gap-x-6 gap-y-1 md:grid-cols-2">
              {COLOR_TOKENS.map((token) => (
                <ColorRow
                  key={token}
                  token={token}
                  value={draft.colors[token] ?? ''}
                  fallback={defaults[`${TOKEN_PREFIX.colors}${token}`] ?? '#000000'}
                  violation={violations[`colors.${token}`]}
                  onChange={(value) => setColor(token, value)}
                  onClear={() => clearColor(token)}
                />
              ))}
            </div>

            {/*
                Naming the refusal rather than leaving a gap. An admin who has just
                coloured twenty tokens will look for the error red, and "it is not in the
                list" is not an answer to why.
            */}
            <p className="mt-3 text-[11.5px]" style={{ color: 'var(--color-faint)' }}>
              {catalog.data?.semanticColors.join(', ') ?? 'error, warn, ok'} are fixed in
              every deployment of this product and cannot be themed. A blocking QC finding
              has to read as blocking whoever the product is sold as.
            </p>
          </Section>

          <Section
            title="type"
            hint={
              catalog.data
                ? `Font stylesheets may be loaded from: ${catalog.data.allowedFontHosts.join(', ') || 'nowhere — name installed families only'}.`
                : ''
            }
          >
            <Field
              label="Stylesheet URL"
              hint="Loaded on every screen. Leave blank to use families already installed on the reader's machine."
              violation={violations['fonts.stylesheetUrl']}
            >
              <input
                className="input input-mono"
                value={draft.fontStylesheetUrl}
                placeholder="https://fonts.googleapis.com/css2?family=…&display=swap"
                onChange={(e) => set('fontStylesheetUrl', e.target.value)}
              />
            </Field>

            <Field
              label="Display"
              hint="Headings and the wordmark. Never body copy."
              violation={violations['fonts.display']}
            >
              <input
                className="input input-mono"
                value={draft.fontDisplay}
                placeholder="Archivo, 'IBM Plex Sans', system-ui, sans-serif"
                onChange={(e) => set('fontDisplay', e.target.value)}
              />
            </Field>

            <Field label="Sans" hint="Interface text." violation={violations['fonts.sans']}>
              <input
                className="input input-mono"
                value={draft.fontSans}
                placeholder="'IBM Plex Sans', system-ui, sans-serif"
                onChange={(e) => set('fontSans', e.target.value)}
              />
            </Field>

            <Field
              label="Mono"
              hint="Every number, code and identifier. Must be monospaced — the grids align on the decimal point."
              violation={violations['fonts.mono']}
            >
              <input
                className="input input-mono"
                value={draft.fontMono}
                placeholder="'IBM Plex Mono', ui-monospace, monospace"
                onChange={(e) => set('fontMono', e.target.value)}
              />
            </Field>
          </Section>

          <Section
            title="remove branding"
            hint="Puts this organization back to the product's own name, mark and palette."
          >
            <button
              className="btn btn-sm"
              onClick={clearTheme}
              disabled={saved === null || save.isPending}
              style={saved === null ? undefined : { color: 'var(--color-error)' }}
            >
              {saved === null ? 'Not branded' : 'Clear this organization’s theme'}
            </button>
          </Section>
        </div>

        {/*
            The lockups, at the two sizes they are actually used at. Not a preview of the
            application — the application is behind this screen and is already themed —
            but of the one thing that is NOT visible from here at its real size.
        */}
        <aside className="lg:sticky lg:top-16 lg:self-start">
          <div
            className="rounded border p-4"
            style={{ borderColor: 'var(--color-rule)', background: 'var(--color-panel)' }}
          >
            <h2 className="col-head mb-3">lockups</h2>

            <div className="mb-4 border-b pb-4" style={{ borderColor: 'var(--color-rule-soft)' }}>
              <p className="mb-2 text-[11px]" style={{ color: 'var(--color-faint)' }}>
                Header, 28px
              </p>
              <BrandLockup size={28} />
            </div>

            <div className="mb-4 border-b pb-4" style={{ borderColor: 'var(--color-rule-soft)' }}>
              <p className="mb-2 text-[11px]" style={{ color: 'var(--color-faint)' }}>
                Sign-in, 40px
              </p>
              <BrandLockupStacked size={40} showTagline />
            </div>

            <div
              className="rounded p-4"
              style={{ background: 'var(--color-primary)' }}
            >
              <p
                className="mb-2 text-[11px] opacity-70"
                style={{ color: 'var(--color-on-primary)' }}
              >
                Reversed
              </p>
              <BrandLockup size={28} onDark />
            </div>

            {/*
                The name and the marks are not previewed live, unlike the colours: they
                are read from the SAVED theme through useBrand, and applying them as you
                type would mean the browser tab and the header changed under an admin who
                had not committed to anything yet.
            */}
            <p className="mt-3 text-[11.5px]" style={{ color: 'var(--color-faint)' }}>
              Colours here are live. The name and marks update once you save.
            </p>
          </div>
        </aside>
      </div>
    </div>
  )
}

function Section({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <section
      className="rounded border"
      style={{ borderColor: 'var(--color-rule)', background: 'var(--color-panel)' }}
    >
      <div
        className="border-b px-4 py-2"
        style={{ borderColor: 'var(--color-rule-soft)', background: 'var(--color-panel-alt)' }}
      >
        <h2 className="col-head">{title}</h2>
        {hint && (
          <p className="mt-0.5 text-[11.5px]" style={{ color: 'var(--color-faint)' }}>
            {hint}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-3 p-4">{children}</div>
    </section>
  )
}

function Field({
  label,
  hint,
  violation,
  children,
}: {
  label: string
  hint?: string
  violation?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="col-head">{label}</span>
      {hint && (
        <span className="mb-1 block text-[11.5px]" style={{ color: 'var(--color-faint)' }}>
          {hint}
        </span>
      )}
      <span className="mt-1 block">{children}</span>
      {violation && (
        <span className="mt-1 block text-[11.5px]" style={{ color: 'var(--color-error)' }} role="alert">
          {violation}
        </span>
      )}
    </label>
  )
}

/**
 * One colour token: a swatch, a hex field, and a way back to the default.
 *
 * The native colour picker and the hex field are bound to the same value, and the hex
 * field is authoritative — the picker cannot express "unset", and clearing the text is
 * how a token goes back to the product default.
 */
function ColorRow({
  token,
  value,
  fallback,
  violation,
  onChange,
  onClear,
}: {
  token: ColorToken
  value: string
  fallback: string
  violation?: string
  onChange: (value: string) => void
  onClear: () => void
}) {
  const { label, hint } = COLOR_LABELS[token]
  const overridden = value.trim().length > 0

  return (
    <div className="flex items-start gap-2 py-1.5">
      <input
        type="color"
        className="mt-0.5 h-7 w-9 shrink-0 cursor-pointer rounded border p-0.5"
        style={{ borderColor: 'var(--color-rule)', background: 'var(--color-panel)' }}
        // The picker needs a concrete colour, so an unset token shows the default it is
        // currently rendering as. Touching it is what makes the token overridden.
        value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : normalizeHex(fallback)}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        aria-label={label}
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[12.5px] font-medium">{label}</span>
          <code className="ident" style={{ color: 'var(--color-faint)' }}>
            {token}
          </code>
          {overridden && (
            <button
              className="text-[11px] underline"
              style={{ color: 'var(--color-accent)' }}
              onClick={onClear}
              title={`Back to ${fallback}`}
            >
              reset
            </button>
          )}
        </div>

        <input
          className="input input-mono mt-1"
          value={value}
          placeholder={fallback}
          spellCheck={false}
          onChange={(e) => onChange(e.target.value)}
        />

        <p className="mt-0.5 text-[11px]" style={{ color: 'var(--color-faint)' }}>
          {hint}
        </p>

        {violation && (
          <p className="mt-0.5 text-[11.5px]" style={{ color: 'var(--color-error)' }} role="alert">
            {violation}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * `<input type="color">` accepts only `#rrggbb`.
 *
 * The stylesheet's own values are all in that form, but a computed value read back from
 * the browser is not guaranteed to be — so anything else falls back to black rather than
 * letting React fight the control over an invalid value on every render.
 */
function normalizeHex(value: string): string {
  const trimmed = value.trim()

  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed

  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    const [r, g, b] = [trimmed[1]!, trimmed[2]!, trimmed[3]!]
    return `#${r}${r}${g}${g}${b}${b}`
  }

  return '#000000'
}
